import {
  DataGrid,
  GridCellModes,
  GridCellModesModel,
  GridCellParams
} from '@mui/x-data-grid';
import styled from 'styled-components';
import COLORS from '../../colors';
import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import { DEFINE_OPTION, DURATION_SECONDS } from '../../constants';
import { updateRow } from '../../redux/features/burst/burstSlice';

interface DefineDurationTableProps {
  option: DEFINE_OPTION;
  burst: DURATION_SECONDS;
}

const StyledTableContainer = styled.div`
  margin: 24px 0;
`;

const DefineDurationTable = ({ burst, option }: DefineDurationTableProps) => {
  const dispatch = useAppDispatch();
  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});
  const { rows } = useAppSelector((state: RootState) => state.burst[burst]);
  const { columns } = useAppSelector((state: RootState) => state.burst);

  const round = (value: number): number => {
    return Number((Math.round(value * 100) / 100).toFixed(2));
  };

  const processRowUpdate = useCallback(
    (newRow: any) => {
      let updatedRow = { ...newRow };

      if (!updatedRow.active) {
        updatedRow = {
          ...updatedRow,
          budget: 0,
          tarps: 0,
          impact: 0
        };

        dispatch(
          updateRow({
            burst,
            rowId: updatedRow.id,
            data: updatedRow
          })
        );

        return updatedRow;
      }

      switch (option) {
        case DEFINE_OPTION.BUDGET:
          if (updatedRow.budget == undefined) updatedRow.budget = 0;

          updatedRow = {
            ...updatedRow,
            tarps: isFinite(updatedRow.budget / updatedRow.cpt)
              ? round(updatedRow.budget / updatedRow.cpt)
              : 0,
            impact: isFinite(updatedRow.budget / updatedRow.cpm)
              ? round(updatedRow.budget / updatedRow.cpm)
              : 0,
            budget: round(updatedRow.budget)
          };
          break;
        case DEFINE_OPTION.TARPS:
          if (updatedRow.tarps == undefined) updatedRow.tarps = 0;

          const budgetForTarps = updatedRow.cpt * updatedRow.tarps;
          updatedRow = {
            ...updatedRow,
            budget: round(budgetForTarps),
            impact: isFinite(budgetForTarps / updatedRow.cpm)
              ? round(budgetForTarps / updatedRow.cpm)
              : 0,
            tarps: round(updatedRow.tarps)
          };
          break;
        case DEFINE_OPTION.ZEROS:
          if (updatedRow.impact == undefined) updatedRow.impact = 0;

          const budgetForImpact = updatedRow.cpm * updatedRow.impact;
          updatedRow = {
            ...updatedRow,
            budget: round(budgetForImpact),
            tarps: isFinite(budgetForImpact / updatedRow.cpt)
              ? round(budgetForImpact / updatedRow.cpt)
              : 0,
            impact: round(updatedRow.impact)
          };
          break;
        default:
          break;
      }

      const updatedReduxRow = updateRow({
        burst,
        rowId: updatedRow.id,
        data: updatedRow
      });

      dispatch(updatedReduxRow);
      return updatedRow;
    },
    [option]
  );

  /**
   * This function is so we can edit cells with "one click".
   * Material UI by usually requires a "double click" to edit cells.
   */
  const handleCellClick = useCallback(
    (params: GridCellParams, event: React.MouseEvent) => {
      if (!params.isEditable) {
        return;
      }

      // Ignore portal
      if (
        (event.target as any).nodeType === 1 &&
        !event.currentTarget.contains(event.target as Element)
      ) {
        return;
      }

      setCellModesModel((prevModel) => {
        return {
          // Revert the mode of the other cells from other rows
          ...Object.keys(prevModel).reduce(
            (acc, id) => ({
              ...acc,
              [id]: Object.keys(prevModel[id]).reduce(
                (acc2, field) => ({
                  ...acc2,
                  [field]: { mode: GridCellModes.View }
                }),
                {}
              )
            }),
            {}
          ),
          [params.id]: {
            // Revert the mode of other cells in the same row
            ...Object.keys(prevModel[params.id] || {}).reduce(
              (acc, field) => ({
                ...acc,
                [field]: { mode: GridCellModes.View }
              }),
              {}
            ),
            [params.field]: { mode: GridCellModes.Edit }
          }
        };
      });
    },
    []
  );

  const handleCellModesModelChange = useCallback(
    (newModel: GridCellModesModel) => {
      setCellModesModel(newModel);
    },
    []
  );

  return (
    <StyledTableContainer>
      <DataGrid
        rows={rows}
        columns={columns}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => console.log(error)}
        sx={{
          color: COLORS.WHITE,
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            borderColor: COLORS.NEUTRAL_800,
            color: COLORS.NEUTRAL_200,
            fontSize: '12px',
            fontWeight: 700,
            lineHeight: '16px'
          },
          '& .MuiDataGrid-r': {
            display: 'none'
          },
          '& .MuiDataGrid-cell': {
            borderColor: COLORS.NEUTRAL_800,
            color: COLORS.NEUTRAL_200,
            display: 'flex',
            width: 'max-content'
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none'
          },
          '& .MuiDataGrid-cell.inactive': {
            color: COLORS.NEUTRAL_700
          },
          '& .MuiDataGrid-cell--editable': {
            color: COLORS.NEUTRAL_200,
            display: 'flex',
            width: '100%'
          },
          '& .MuiDataGrid-cell--editable .MuiDataGrid-cellContent': {
            border: `1px solid ${COLORS.NEUTRAL_700}`,
            borderRadius: '4px',
            padding: '8px 16px',
            display: 'flex',
            width: '100%',
            justifyContent: 'center'
          },
          '& .MuiDataGrid-footerContainer': {
            borderColor: COLORS.NEUTRAL_800
          },
          '& .MuiDataGrid-booleanCell[data-value="true"]': {
            color: COLORS.NEUTRAL_200,
            borderRadius: '4px',
            border: `1px solid ${COLORS.NEUTRAL_700}`
          },
          '& .MuiDataGrid-booleanCell[data-value="false"]': {
            color: COLORS.NEUTRAL_700,
            borderRadius: '4px',
            border: `1px solid ${COLORS.NEUTRAL_700}`
          },
          '& .MuiTablePagination-input': {
            color: COLORS.NEUTRAL_200
          },
          '& .MuiTablePagination-input .MuiSelect-icon': {
            color: COLORS.NEUTRAL_200
          },
          '& .MuiTablePagination-actions button': {
            color: COLORS.NEUTRAL_200
          },
          '& .MuiTablePagination-actions button.Mui-disabled': {
            color: COLORS.NEUTRAL_700
          },
          '& .MuiDataGrid-cell.MuiDataGrid-cell--editing': {
            backgroundColor: 'transparent'
          },
          '& .MuiCheckbox-root': {
            color: `${COLORS.NEUTRAL_700}`
          },
          '& .MuiDataGrid-editInputCell': {
            color: `${COLORS.NEUTRAL_700}`
          }
        }}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 5 }
          }
        }}
        getCellClassName={(params: GridCellParams<any, any, number>) => {
          return params.row.active === true ? 'active' : 'inactive';
        }}
        pageSizeOptions={[5, 15, { value: rows.length, label: 'All' }]}
        cellModesModel={cellModesModel}
        onCellModesModelChange={handleCellModesModelChange}
        onCellClick={handleCellClick}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
      />
    </StyledTableContainer>
  );
};

export default DefineDurationTable;
